import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { HOME_ICON_SHAPES, IconShape } from './icon-data';

/**
 * Renders a decorative home-page icon from the static HOME_ICON_SHAPES registry.
 * Always paired with a visible text label in this page, so the SVG is aria-hidden.
 * Shapes are rendered as real SVG child elements (not innerHTML) so this works
 * under SSR/prerendering, where SVGSVGElement.innerHTML isn't implemented.
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  template: `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
      @for (s of shapes; track $index) {
        @switch (s.tag) {
          @case ('path') {
            <path [attr.d]="s.d" [attr.fill]="s.fill" [attr.stroke]="s.stroke" [attr.opacity]="s.opacity" />
          }
          @case ('circle') {
            <circle [attr.cx]="s.cx" [attr.cy]="s.cy" [attr.r]="s.r" [attr.fill]="s.fill" [attr.stroke]="s.stroke" />
          }
          @case ('ellipse') {
            <ellipse [attr.cx]="s.cx" [attr.cy]="s.cy" [attr.rx]="s.rx" [attr.ry]="s.ry" />
          }
          @case ('rect') {
            <rect [attr.x]="s.x" [attr.y]="s.y" [attr.width]="s.width" [attr.height]="s.height" [attr.rx]="s.rx" [attr.opacity]="s.opacity" />
          }
          @case ('line') {
            <line [attr.x1]="s.x1" [attr.y1]="s.y1" [attr.x2]="s.x2" [attr.y2]="s.y2" />
          }
          @case ('polyline') {
            <polyline [attr.points]="s.points" />
          }
        }
      }
    </svg>
  `,
  styles: `
    :host { display: inline-flex; align-items: center; justify-content: center; }
    svg { width: 100%; height: 100%; }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppIcon {
  @Input({ required: true }) name = '';

  get shapes(): IconShape[] {
    return HOME_ICON_SHAPES[this.name] ?? [];
  }
}
